import { appClient } from 'api/apiClient';
import { buildFileFormData, buildPageQuery, type Paginated } from 'api/v1Helpers';
import {
    normalizeGroup,
    normalizeGroupMember,
    type ApiGroup,
    type ApiGroupMember,
    type AnnouncementGroup,
    type AnnouncementGroupMemberRow,
    type CreateGroupPayload,
    type UpdateGroupPayload,
} from 'types/announcementsV1';

export async function listGroups(parishId: string, opts?: { page?: number; pageSize?: number }): Promise<Paginated<AnnouncementGroup>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 50);
    const page = await appClient.get<Paginated<ApiGroup>>(`parishes/${parishId}/groups?${params.toString()}`);
    return {
        items: (page.items || []).map((g) => normalizeGroup(g)),
        total: page.total ?? 0,
        page: page.page ?? opts?.page ?? 1,
        pageSize: page.pageSize ?? opts?.pageSize ?? 50,
    };
}

export async function getGroup(parishId: string, groupId: string): Promise<AnnouncementGroup> {
    const res = await appClient.get<ApiGroup>(`parishes/${parishId}/groups/${groupId}`);
    return normalizeGroup(res);
}

export async function createGroup(parishId: string, payload: CreateGroupPayload): Promise<AnnouncementGroup> {
    const res = await appClient.post<ApiGroup>(`parishes/${parishId}/groups`, payload);
    return normalizeGroup(res);
}

export async function updateGroup(parishId: string, groupId: string, payload: UpdateGroupPayload): Promise<AnnouncementGroup> {
    const res = await appClient.patch<ApiGroup>(`parishes/${parishId}/groups/${groupId}`, payload);
    return normalizeGroup(res);
}

export async function deleteGroup(parishId: string, groupId: string): Promise<void> {
    await appClient.delete(`parishes/${parishId}/groups/${groupId}`);
}

export async function duplicateGroup(parishId: string, groupId: string): Promise<AnnouncementGroup> {
    const res = await appClient.post<ApiGroup>(`parishes/${parishId}/groups/${groupId}/duplicate`, {});
    return normalizeGroup(res);
}

export async function uploadGroupImage(parishId: string, groupId: string, file: Blob): Promise<AnnouncementGroup> {
    const form = buildFileFormData(file);
    const res = await appClient.put<ApiGroup>(`parishes/${parishId}/groups/${groupId}/image`, form);
    return normalizeGroup(res);
}

export async function listGroupMembers(
    parishId: string,
    groupId: string,
    opts?: { page?: number; pageSize?: number }
): Promise<Paginated<AnnouncementGroupMemberRow>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 100);
    const page = await appClient.get<Paginated<ApiGroupMember>>(`parishes/${parishId}/groups/${groupId}/members?${params.toString()}`);
    return {
        items: (page.items || []).map(normalizeGroupMember),
        total: page.total ?? 0,
        page: page.page ?? opts?.page ?? 1,
        pageSize: page.pageSize ?? opts?.pageSize ?? 100,
    };
}

export async function putGroupMember(parishId: string, groupId: string, userId: string): Promise<void> {
    await appClient.put(`parishes/${parishId}/groups/${groupId}/members/${userId}`, {});
}

export async function removeGroupMember(parishId: string, groupId: string, userId: string): Promise<void> {
    await appClient.delete(`parishes/${parishId}/groups/${groupId}/members/${userId}`);
}
